import { Link, NavLink, useNavigate } from "react-router-dom";
import { Phone, MessageSquare, Menu, X, Snowflake } from "lucide-react";
import { useState } from "react";
import { COMPANY } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useSettings } from "@/contexts/SettingsContext";
import { onCallClick, onTextClick } from "@/lib/dialer";
import LangSelector from "./LangSelector";

const navItems = [
  { to: "/", key: "home" },
  { to: "/#services", key: "services" },
  { to: "/#brands", key: "brands" },
  { to: "/#pricing", key: "pricing" },
  { to: "/#specials", key: "specials" },
  { to: "/#projects", key: "projects" },
  { to: "/#reviews", key: "reviews" },
  { to: "/areas", key: "areas" },
  { to: "/#about", key: "about" },
  { to: "/schedule", key: "schedule" },
  { to: "/contact", key: "contact" },
];

function Logo({ small=false }) {
  const { settings } = useSettings();
  if (settings.logo) {
    return <img src={settings.logo} alt="AIR-COLD HVAC" className={`${small ? "h-10" : "h-12"} w-auto rounded-md object-contain`} data-testid="brand-logo-img"/>;
  }
  return (
    <div className="flex items-center gap-2.5" data-testid="brand-logo-fallback">
      <div className={`${small ? "size-8" : "size-9"} rounded-xl bg-ice-400/15 grid place-items-center ring-1 ring-ice-400/40`}>
        <Snowflake className="size-5 text-ice-400" />
      </div>
      <div className="leading-tight">
        <div className="font-display font-black text-[15px] tracking-wide">AIR-COLD</div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-silver-400">HVAC</div>
      </div>
    </div>
  );
}

export default function Nav() {
  const [open, setOpen] = useState(false);
  const { t } = useLang();
  const nav = useNavigate();

  return (
    <header className="sticky top-0 z-50 glass" data-testid="site-nav">
      <div className="mx-auto max-w-7xl px-5 md:px-8 h-16 md:h-[72px] flex items-center justify-between gap-3">
        <Link to="/" data-testid="nav-logo" className="shrink-0"><Logo/></Link>

        <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center">
          {navItems.map(item => (
            <NavLink
              key={item.key}
              to={item.to}
              data-testid={`nav-${item.key}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm rounded-full transition-colors whitespace-nowrap ${isActive ? "text-ice-400" : "text-silver-300 hover:text-white"}`
              }
            >
              {t(`nav.${item.key}`)}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2 shrink-0">
          <LangSelector />
          <a href={`tel:${COMPANY.phoneTel}`} onClick={onCallClick} data-testid="nav-call-btn" className="btn-primary px-4 py-2 rounded-full text-sm flex items-center gap-2 whitespace-nowrap">
            <Phone className="size-4" /> {COMPANY.phone}
          </a>
        </div>

        <button onClick={() => setOpen(true)} className="lg:hidden p-2" data-testid="nav-open-menu" aria-label="Open menu">
          <Menu className="size-6" />
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-[60] bg-ink-900 lg:hidden" data-testid="mobile-menu">
          <div className="flex items-center justify-between px-5 h-16">
            <Logo small/>
            <button onClick={() => setOpen(false)} data-testid="nav-close-menu" aria-label="Close menu"><X className="size-6" /></button>
          </div>
          <div className="px-5 pb-8 grid gap-1">
            {navItems.map(item => (
              <button
                key={item.key}
                data-testid={`mnav-${item.key}`}
                onClick={() => { setOpen(false); nav(item.to); }}
                className="text-left px-3 py-3 rounded-xl hover:bg-white/5 text-silver-300"
              >
                {t(`nav.${item.key}`)}
              </button>
            ))}
            <div className="grid grid-cols-2 gap-3 mt-4">
              <a href={`tel:${COMPANY.phoneTel}`} onClick={onCallClick} className="btn-primary py-3 rounded-full text-sm flex items-center justify-center gap-2"><Phone className="size-4" /> {t("cta.call")}</a>
              <a href={COMPANY.sms} onClick={onTextClick} className="btn-ghost py-3 rounded-full text-sm flex items-center justify-center gap-2"><MessageSquare className="size-4" /> {t("cta.text")}</a>
            </div>
            <div className="mt-4"><LangSelector /></div>
          </div>
        </div>
      )}
    </header>
  );
}
